/**
 * PersonMemorialOverviewCard — tasteful Overview CTA for Memorial Mode.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Flame, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { getApiBase } from '../../utils/apiBase';
import {
  getMemorialPublicUrl,
  isMemorialActive,
  isMemorialPublished,
  memorialManageHref,
  MEMORIAL_COPY,
} from '../../utils/memorial';

const API = getApiBase();

export function PersonMemorialOverviewCard({ person }) {
  const [workspace, setWorkspace] = useState(null);

  useEffect(() => {
    if (!person?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/memorials-api/by-person/${person.id}`);
        if (!cancelled) setWorkspace(data);
      } catch {
        if (!cancelled) setWorkspace(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [person?.id, person?.memorial_summary?.memorial_active]);

  if (!person) return null;

  const memorial = workspace?.memorial;
  const published = isMemorialPublished(memorial);
  const publicUrl = getMemorialPublicUrl(workspace?.public_url, memorial?.slug);
  const active = isMemorialActive(person);

  let title = 'Memorial Mode';
  let body = 'Create a private memorial space when the time is right.';
  let workspaceLabel = 'Prepare memorial';

  if (published && publicUrl) {
    title = 'Memorial page published';
    body = 'A public memorial page is available when you choose to share it.';
    workspaceLabel = 'Manage memorial';
  } else if (active || (memorial && memorial.status === 'draft')) {
    title = 'Memorial draft';
    body = 'Your private memorial workspace is ready — publish only when you feel the time is right.';
    workspaceLabel = 'Open memorial workspace';
  }

  return (
    <section
      className="min-w-0 rounded-xl border border-[hsl(38_14%_88%)]/75 bg-gradient-to-br from-[hsl(42_30%_99%)] to-[hsl(40_22%_97%)] px-4 py-4 sm:px-5 sm:py-5"
      data-testid="overview-memorial-card"
    >
      <div className="flex min-w-0 flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary)/0.08)] text-primary">
            <Flame className="h-4 w-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
              {title}
            </p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground/90">{body}</p>
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground/65">
              Marking someone as deceased does not automatically publish a memorial. {MEMORIAL_COPY.tributesModerated}
            </p>
          </div>
        </div>
        <div className="flex w-full min-w-0 shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap xl:w-auto xl:max-w-[16rem] xl:flex-col">
          {published && publicUrl ? (
            <>
              <Button type="button" size="sm" className="w-full rounded-full sm:w-auto xl:w-full" asChild>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="overview-memorial-view-public">
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                  View public memorial
                </a>
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full rounded-full gap-1 sm:w-auto xl:w-full"
                asChild
                data-testid="overview-memorial-cta"
              >
                <Link to={memorialManageHref(person)}>
                  {workspaceLabel}
                  <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                </Link>
              </Button>
            </>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="default"
              className="w-full rounded-full gap-1 sm:w-auto xl:w-full"
              asChild
              data-testid="overview-memorial-cta"
            >
              <Link to={memorialManageHref(person)}>
                {workspaceLabel}
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

export default PersonMemorialOverviewCard;
